import { useState, useEffect } from 'react';
import { Calendar, User, CheckCircle, XCircle, AlertCircle, Clock, FileText } from '../../icons/wp-icons';
import { getOffeneAntraege, genehmigenAbwesenheit, ablehnenAbwesenheit } from '../../api/abwesenheitApi';
import Modal from '../Common/Modal';

const TYP_CONFIG = {
  URLAUB: { label: 'Urlaub', color: 'bg-green-100 text-green-700', icon: '🏖️' },
  KRANKHEIT: { label: 'Krankheit', color: 'bg-red-100 text-red-700', icon: '🤒' },
  FORTBILDUNG: { label: 'Fortbildung', color: 'bg-blue-100 text-blue-700', icon: '📚' },
  HOMEOFFICE: { label: 'Homeoffice', color: 'bg-orange-100 text-orange-700', icon: '🏠' },
  SONSTIGES: { label: 'Sonstiges', color: 'bg-gray-100 text-gray-700', icon: '📝' }
};

function AbwesenheitAntraege({ onRefresh }) {
  const [antraege, setAntraege] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(null);

  // Ablehnung Modal
  const [ablehnungModal, setAblehnungModal] = useState(null);
  const [ablehnungsgrund, setAblehnungsgrund] = useState('');

  useEffect(() => {
    loadAntraege();
  }, []);

  const loadAntraege = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getOffeneAntraege();
      setAntraege(data);
    } catch (err) {
      console.error('Fehler:', err);
      setError('Anträge konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const handleGenehmigen = async (id) => {
    try {
      setProcessing(id);
      await genehmigenAbwesenheit(id);
      await loadAntraege();
      onRefresh?.();
    } catch (err) {
      console.error('Fehler:', err);
      alert(err.response?.data?.error || 'Fehler beim Genehmigen');
    } finally {
      setProcessing(null);
    }
  };

  const handleAblehnenClick = (antrag) => {
    setAblehnungModal(antrag);
    setAblehnungsgrund('');
  };

  const handleAblehnenSubmit = async () => {
    if (!ablehnungModal) return;

    try {
      setProcessing(ablehnungModal.id);
      await ablehnenAbwesenheit(ablehnungModal.id, ablehnungsgrund);
      setAblehnungModal(null);
      setAblehnungsgrund('');
      await loadAntraege();
      onRefresh?.();
    } catch (err) {
      console.error('Fehler:', err);
      alert(err.response?.data?.error || 'Fehler beim Ablehnen');
    } finally {
      setProcessing(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateRange = (start, end) => {
    const startStr = formatDate(start);
    const endStr = formatDate(end);
    if (startStr === endStr) {
      return startStr;
    }
    return `${startStr} - ${endStr}`;
  };

  // Berechne Arbeitstage
  const berechneArbeitstage = (start, end) => {
    let count = 0;
    const current = new Date(start);
    const endDate = new Date(end);

    while (current <= endDate) {
      const dayOfWeek = current.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        count++;
      }
      current.setDate(current.getDate() + 1);
    }

    return count;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-start gap-2 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
        <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
        {error}
      </div>
    );
  }

  if (antraege.length === 0) {
    return (
      <div className="bg-surface rounded-card shadow-card p-8 text-center">
        <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium mb-2">Keine offenen Anträge</h3>
        <p className="text-text-muted">Alle Abwesenheitsanträge wurden bearbeitet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Offene Anträge</h2>
        <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium">
          {antraege.length} {antraege.length === 1 ? 'Antrag' : 'Anträge'}
        </span>
      </div>

      {antraege.map(antrag => {
        const typConfig = TYP_CONFIG[antrag.typ] || TYP_CONFIG.SONSTIGES;
        const arbeitstage = berechneArbeitstage(antrag.startDatum, antrag.endDatum);
        const isProcessing = processing === antrag.id;

        return (
          <div key={antrag.id} className="bg-surface rounded-card shadow-card p-5 border-l-4 border-yellow-400">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
              <div className="flex-1">
                {/* Header */}
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-gray-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{antrag.benutzer.name}</h3>
                    <p className="text-sm text-text-muted">{antrag.benutzer.email}</p>
                  </div>
                </div>

                {/* Typ */}
                <div className="flex items-center gap-2 mb-3">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${typConfig.color}`}>
                    {typConfig.icon} {typConfig.label}
                  </span>
                  <span className="text-sm text-text-muted">
                    {arbeitstage} {arbeitstage === 1 ? 'Arbeitstag' : 'Arbeitstage'}
                  </span>
                </div>

                {/* Zeitraum */}
                <div className="flex items-center gap-2 text-sm mb-2">
                  <Calendar className="w-4 h-4 text-text-muted" />
                  <span className="font-medium">{formatDateRange(antrag.startDatum, antrag.endDatum)}</span>
                  {!antrag.ganztags && antrag.startzeit && antrag.endzeit && (
                    <span className="text-text-muted">
                      <Clock className="w-3 h-3 inline mr-1" />
                      {antrag.startzeit} - {antrag.endzeit}
                    </span>
                  )}
                </div>

                {/* Grund */}
                {antrag.grund && (
                  <div className="flex items-start gap-2 text-sm mt-3 p-3 bg-gray-50 rounded-lg">
                    <FileText className="w-4 h-4 text-text-muted mt-0.5 shrink-0" />
                    <p className="text-text-muted">{antrag.grund}</p>
                  </div>
                )}

                {/* Beantragt am */}
                <p className="text-xs text-text-muted mt-3">
                  Beantragt am {formatDate(antrag.erstelltAm)}
                </p>
              </div>

              {/* Aktionen */}
              <div className="flex sm:flex-col gap-2">
                <button
                  onClick={() => handleGenehmigen(antrag.id)}
                  disabled={isProcessing}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-button font-medium hover:bg-green-700 transition disabled:opacity-50 flex-1 sm:flex-auto"
                >
                  <CheckCircle className={`w-4 h-4 ${isProcessing ? 'animate-spin' : ''}`} />
                  Genehmigen
                </button>
                <button
                  onClick={() => handleAblehnenClick(antrag)}
                  disabled={isProcessing}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-button font-medium hover:bg-red-700 transition disabled:opacity-50 flex-1 sm:flex-auto"
                >
                  <XCircle className="w-4 h-4" />
                  Ablehnen
                </button>
              </div>
            </div>
          </div>
        );
      })}

      {/* Ablehnung Modal */}
      <Modal
        isOpen={!!ablehnungModal}
        onClose={() => setAblehnungModal(null)}
        title="Antrag ablehnen"
      >
        {ablehnungModal && (
          <div className="space-y-4">
            <p className="text-text-muted">
              Sie lehnen den {TYP_CONFIG[ablehnungModal.typ]?.label || 'Abwesenheits'}-Antrag von{' '}
              <strong>{ablehnungModal.benutzer.name}</strong> ab.
            </p>

            <div>
              <label className="block text-sm font-medium mb-2">
                Ablehnungsgrund (optional)
              </label>
              <textarea
                value={ablehnungsgrund}
                onChange={(e) => setAblehnungsgrund(e.target.value)}
                rows="3"
                className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="z.B. Personalmangel, bereits zu viele Abwesenheiten im Zeitraum..."
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={handleAblehnenSubmit}
                disabled={processing}
                className="flex-1 bg-red-600 text-white py-3 rounded-button font-medium hover:bg-red-700 transition disabled:opacity-50"
              >
                {processing ? 'Wird abgelehnt...' : 'Ablehnen'}
              </button>
              <button
                onClick={() => setAblehnungModal(null)}
                className="flex-1 bg-gray-200 text-text py-3 rounded-button font-medium hover:bg-gray-300 transition"
              >
                Abbrechen
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default AbwesenheitAntraege;
